import { createSelector } from 'reselect';
import currency from 'currency.js';
import { accountsByIdSelector } from './accounts';
import { periodsByIdSelector } from './periods';
import { groupsByIdSelector } from './groups';
import { consolidationsSelector } from './consolidations';
import { getGroupSubsidiaryPrefix } from 'utils/groups';

export const journalEntriesByIdSelector = (state) => state.journal.entriesById;

const journalEntryIdsSelector = createSelector(
  journalEntriesByIdSelector,
  (journalEntriesById) => Object.keys(journalEntriesById)
);

const journalEntriesSelector = createSelector(
  journalEntryIdsSelector,
  journalEntriesByIdSelector,
  periodsByIdSelector,
  (journalEntryIds, journalEntriesById, periodsById) => {
    return journalEntryIds.map((id) => ({
      id: parseInt(id, 10),
      periodName: periodsById[journalEntriesById[id].periodId].name,
      ...journalEntriesById[id],
    }));
  }
);

export const journalEntryItemsByIdSelector = (state) =>
  state.journal.entryItemsById;

const journalEntryItemIdsSelector = createSelector(
  journalEntryItemsByIdSelector,
  (journalEntryItemsById) => Object.keys(journalEntryItemsById)
);

export const journalEntryItemsSelector = createSelector(
  journalEntryItemIdsSelector,
  journalEntryItemsByIdSelector,
  accountsByIdSelector,
  groupsByIdSelector,
  consolidationsSelector,
  (itemIds, itemsById, accountsById, groupsById, consolidations) => {
    return itemIds.map((id) => {
      const item = itemsById[id];
      const content =
        item.contentType === 'account'
          ? accountsById[item.contentId]
          : groupsById[item.contentId];

      let subPrefix = '';
      if (item.contentType === 'group') {
        subPrefix = `${getGroupSubsidiaryPrefix(content, consolidations)} `;
      }

      const contentHasNumber = !!content.number;
      const contentDescription =
        item.contentType === 'account'
          ? contentHasNumber
            ? `${content.number} ${content.description}`
            : content.description
          : `${subPrefix}${content.name}`;

      return {
        id: parseInt(id, 10),
        ...item,
        contentDescription,
      };
    });
  }
);

/**
 * entryId: {
 *    id,
 *    type: "aje" or "rje"
 *    description
 *    status: "posted" or "unposted"
 *    modified
 *    periodId,
 *    periodName,
 *    total,
 *    totalCredit,
 *    totalDebit,
 *    items: {
 *      itemId: {
 *        id
 *        entryId
 *        contentType: "account" or "group"
 *        debit
 *        credit
 *        contentDescription: - account description or group name
 *      }...
 *    }
 *  }
 */
export const journalEntriesWithItemsSelector = createSelector(
  journalEntriesSelector,
  journalEntryItemsSelector,
  (journalEntries, journalEntryItems) => {
    const journalEntriesWithItems = journalEntries.map((entry) => ({
      ...entry,
      groupPath: getJournalEntryGroupPath(entry),
      total: currency(0),
      totalDebit: currency(0),
      totalCredit: currency(0),
      items: {},
    }));

    journalEntryItems.forEach((entryItem) => {
      let entry = journalEntriesWithItems.find(
        (entry) => entry.id === entryItem.entryId
      );
      entry.total = entry.total.add(entryItem.debit).subtract(entryItem.credit);
      entry.totalDebit = entry.totalDebit.add(entryItem.debit);
      entry.totalCredit = entry.totalCredit.add(entryItem.credit);
      entry.items[entryItem.id] = entryItem;
    });

    return journalEntriesWithItems;
  }
);

export const journalDetailListingSelector = createSelector(
  journalEntriesWithItemsSelector,
  (entries) => {
    return entries.reduce((result, entry) => {
      result.push({
        ...entry,
        groupPath: getJournalEntryGroupPath(entry),
      });

      Object.values(entry.items).forEach((entryItem) => {
        result.push({
          ...entryItem,
          groupPath: getJournalEntryItemGroupPath(entry, entryItem),
          description: entryItem.contentDescription,
        });
      });

      return result;
    }, []);
  }
);

function getJournalEntryGroupPath(journalEntry) {
  return [getJournalEntryGroupPathId(journalEntry)];
}

function getJournalEntryItemGroupPath(journalEntry, journalEntryItem) {
  return [
    getJournalEntryGroupPathId(journalEntry),
    getJournalEntryItemGroupPathId(journalEntryItem),
  ];
}

function getJournalEntryGroupPathId(journalEntry) {
  return `${journalEntry.id}-${journalEntry.type}`;
}

function getJournalEntryItemGroupPathId(journalEntryItem) {
  return `${journalEntryItem.contentType}-${journalEntryItem.id}-${journalEntryItem.contentId}`;
}
